import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { api } from '@/utils/api';
import { toast } from 'sonner';
import { CheckCircle, XCircle, Eye, Edit, LogOut } from 'lucide-react';

export default function PendingApprovals() {
  const [visits, setVisits] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedVisit, setSelectedVisit] = useState(null);
  const [guardName, setGuardName] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const [actionType, setActionType] = useState(''); // 'entry', 'exit', 'view', 'edit'
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editFormData, setEditFormData] = useState({});

  useEffect(() => {
    loadCurrentUser();
    loadData();
  }, []);

  const loadCurrentUser = async () => {
    try {
      const response = await api.getMe();
      setCurrentUser(response.data);
      setGuardName(response.data.name); // Auto-fill with logged-in user's name
    } catch (error) {
      console.error('Error loading current user:', error);
    }
  };

  const loadData = async () => {
    try {
      const [visitsRes, deptsRes, empsRes] = await Promise.all([
        api.getVisits(),
        api.getDepartments(),
        api.getEmployees()
      ]);
      setVisits(visitsRes.data);
      setDepartments(deptsRes.data);
      setEmployees(empsRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Error al cargar datos');
    }
  };

  const getDepartmentName = (deptId) => {
    const dept = departments.find(d => d.id === deptId);
    return dept ? dept.name : 'N/A';
  };

  const getEmployeeName = (empId) => {
    const emp = employees.find(e => e.id === empId);
    return emp ? emp.name : 'N/A';
  };

  const openDialog = (visit, type) => {
    setSelectedVisit(visit);
    setActionType(type);
    // Reset guard name to current user's name for each dialog
    if (currentUser) {
      setGuardName(currentUser.name);
    }
    if (type === 'edit') {
      setEditFormData({
        visitor_name: visit.visitor_name,
        visitor_company: visit.visitor_company,
        visitor_contact: visit.visitor_contact || '',
        subject: visit.subject,
        companions: visit.companions || '',
        has_vehicle: visit.has_vehicle,
        vehicle_plates: visit.vehicle_plates || ''
      });
    }
    setDialogOpen(true);
  };

  const handleApproval = async () => {
    if (!guardName.trim()) {
      toast.error('Ingrese su nombre');
      return;
    }

    setLoading(true);
    try {
      if (actionType === 'entry') {
        await api.approveEntry(selectedVisit.id, guardName);
        toast.success('Entrada aprobada');
      } else if (actionType === 'exit') {
        await api.approveExit(selectedVisit.id, guardName);
        toast.success('Salida registrada exitosamente');
      }
      setDialogOpen(false);
      loadData();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al aprobar');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async () => {
    setLoading(true);
    try {
      await api.updateVisit(selectedVisit.id, editFormData);
      toast.success('Información actualizada');
      setDialogOpen(false);
      loadData();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al actualizar');
    } finally {
      setLoading(false);
    }
  };

  const pendingEntry = visits.filter(v => v.status === 'pending_entry');
  const incompleteRecords = visits.filter(v => v.status === 'approved_entry');

  return (
    <DashboardLayout>
      <div className="max-w-7xl" data-testid="pending-approvals-page">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 uppercase tracking-tight">Gestión de Visitas</h1>
          <p className="text-gray-600 mt-1">Aprobaciones y registros de entrada/salida</p>
        </div>

        {/* Pending Entry */}
        <div className="mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4 uppercase tracking-tight flex items-center gap-2">
            <XCircle className="h-6 w-6 text-orange-500" />
            Entradas Pendientes de Aprobación ({pendingEntry.length})
          </h2>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            {pendingEntry.length === 0 ? (
              <div className="p-12 text-center text-gray-500">
                <p>No hay entradas pendientes</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Visitante</th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Empresa</th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Departamento</th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Empleado</th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Hora</th>
                      <th className="px-6 py-4 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Acción</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {pendingEntry.map((visit) => (
                      <tr key={visit.id} className="hover:bg-gray-50 transition-colors" data-testid="pending-entry-row">
                        <td className="px-6 py-4 text-sm text-gray-900 font-medium">{visit.visitor_name}</td>
                        <td className="px-6 py-4 text-sm text-gray-600">{visit.visitor_company}</td>
                        <td className="px-6 py-4 text-sm text-gray-600">{getDepartmentName(visit.department_id)}</td>
                        <td className="px-6 py-4 text-sm text-gray-600">{getEmployeeName(visit.employee_id)}</td>
                        <td className="px-6 py-4 text-sm text-gray-600">{new Date(visit.entry_datetime).toLocaleString('es-ES')}</td>
                        <td className="px-6 py-4 text-center">
                          <Button
                            size="sm"
                            data-testid="approve-entry-button"
                            onClick={() => openDialog(visit, 'entry')}
                            className="bg-green-600 hover:bg-green-700 text-white font-medium uppercase tracking-wide"
                          >
                            Aprobar Entrada
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Incomplete Records (Inside) */}
        <div>
          <h2 className="text-xl font-bold text-gray-900 mb-4 uppercase tracking-tight flex items-center gap-2">
            <CheckCircle className="h-6 w-6 text-blue-500" />
            Registros Incompletos ({incompleteRecords.length})
          </h2>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            {incompleteRecords.length === 0 ? (
              <div className="p-12 text-center text-gray-500">
                <p>No hay visitantes dentro de la empresa</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nombre</th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Empresa</th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha de Entrada</th>
                      <th className="px-6 py-4 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Acceso E/S</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {incompleteRecords.map((visit) => (
                      <tr key={visit.id} className="hover:bg-gray-50 transition-colors" data-testid="incomplete-record-row">
                        <td className="px-6 py-4 text-sm text-gray-900 font-medium">{visit.visitor_name}</td>
                        <td className="px-6 py-4 text-sm text-gray-600">{visit.visitor_company}</td>
                        <td className="px-6 py-4 text-sm text-gray-600">{new Date(visit.entry_datetime).toLocaleDateString('es-ES')}</td>
                        <td className="px-6 py-4 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              data-testid="view-details-button"
                              onClick={() => openDialog(visit, 'view')}
                              className="border-blue-500 text-blue-600 hover:bg-blue-50"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              data-testid="edit-visit-button"
                              onClick={() => openDialog(visit, 'edit')}
                              className="border-orange-500 text-orange-600 hover:bg-orange-50"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              data-testid="approve-exit-button"
                              onClick={() => openDialog(visit, 'exit')}
                              className="bg-[#DC143C] hover:bg-[#B91C1C] text-white"
                            >
                              <LogOut className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Dialogs */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold uppercase tracking-tight">
                {actionType === 'entry' && 'Aprobar Entrada'}
                {actionType === 'exit' && 'Registrar Salida'}
                {actionType === 'view' && 'Detalles de la Visita'}
                {actionType === 'edit' && 'Editar Información'}
              </DialogTitle>
            </DialogHeader>
            
            {selectedVisit && (
              <div className="space-y-4 mt-4">
                {/* View Details */}
                {actionType === 'view' && (
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm font-medium text-gray-500 uppercase">Nombre</p>
                        <p className="text-base text-gray-900">{selectedVisit.visitor_name}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-500 uppercase">Empresa</p>
                        <p className="text-base text-gray-900">{selectedVisit.visitor_company}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-500 uppercase">Asunto</p>
                        <p className="text-base text-gray-900">{selectedVisit.subject}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-500 uppercase">Acompañantes</p>
                        <p className="text-base text-gray-900">{selectedVisit.companions || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-500 uppercase">Vehículo</p>
                        <p className="text-base text-gray-900">{selectedVisit.has_vehicle ? 'Sí' : 'No'}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-500 uppercase">Placas</p>
                        <p className="text-base text-gray-900">{selectedVisit.vehicle_plates || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-500 uppercase">Fecha de Entrada</p>
                        <p className="text-base text-gray-900">{new Date(selectedVisit.entry_datetime).toLocaleDateString('es-ES')}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-500 uppercase">Hora de Entrada</p>
                        <p className="text-base text-gray-900">{new Date(selectedVisit.entry_datetime).toLocaleTimeString('es-ES')}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Edit Form */}
                {actionType === 'edit' && (
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="edit_name">Nombre del Visitante</Label>
                      <Input
                        id="edit_name"
                        value={editFormData.visitor_name}
                        onChange={(e) => setEditFormData({ ...editFormData, visitor_name: e.target.value })}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="edit_company">Empresa</Label>
                      <Input
                        id="edit_company"
                        value={editFormData.visitor_company}
                        onChange={(e) => setEditFormData({ ...editFormData, visitor_company: e.target.value })}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="edit_subject">Asunto</Label>
                      <Input
                        id="edit_subject"
                        value={editFormData.subject}
                        onChange={(e) => setEditFormData({ ...editFormData, subject: e.target.value })}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="edit_companions">Acompañantes</Label>
                      <Input
                        id="edit_companions"
                        value={editFormData.companions}
                        onChange={(e) => setEditFormData({ ...editFormData, companions: e.target.value })}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="edit_plates">Placas del Vehículo</Label>
                      <Input
                        id="edit_plates"
                        value={editFormData.vehicle_plates}
                        onChange={(e) => setEditFormData({ ...editFormData, vehicle_plates: e.target.value })}
                        className="mt-1"
                      />
                    </div>
                    <div className="flex gap-2 justify-end pt-2">
                      <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
                      <Button
                        onClick={handleEdit}
                        disabled={loading}
                        className="bg-[#DC143C] hover:bg-[#B91C1C] text-white"
                      >
                        {loading ? 'Guardando...' : 'Guardar Cambios'}
                      </Button>
                    </div>
                  </div>
                )}

                {/* Exit Form */}
                {actionType === 'exit' && (
                  <div className="space-y-4">
                    <div className="bg-gray-50 p-4 rounded-md space-y-2 text-sm">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <p className="font-medium text-gray-500 uppercase">Nombre</p>
                          <p className="text-gray-900">{selectedVisit.visitor_name}</p>
                        </div>
                        <div>
                          <p className="font-medium text-gray-500 uppercase">Empresa</p>
                          <p className="text-gray-900">{selectedVisit.visitor_company}</p>
                        </div>
                        <div>
                          <p className="font-medium text-gray-500 uppercase">Acompañantes</p>
                          <p className="text-gray-900">{selectedVisit.companions || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="font-medium text-gray-500 uppercase">Asunto</p>
                          <p className="text-gray-900">{selectedVisit.subject}</p>
                        </div>
                        <div>
                          <p className="font-medium text-gray-500 uppercase">Vehículo</p>
                          <p className="text-gray-900">{selectedVisit.has_vehicle ? 'Sí' : 'No'}</p>
                        </div>
                        <div>
                          <p className="font-medium text-gray-500 uppercase">Placas</p>
                          <p className="text-gray-900">{selectedVisit.vehicle_plates || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="font-medium text-gray-500 uppercase">Fecha de Entrada</p>
                          <p className="text-gray-900">{new Date(selectedVisit.entry_datetime).toLocaleDateString('es-ES')}</p>
                        </div>
                        <div>
                          <p className="font-medium text-gray-500 uppercase">Hora de Entrada</p>
                          <p className="text-gray-900">{new Date(selectedVisit.entry_datetime).toLocaleTimeString('es-ES')}</p>
                        </div>
                        <div>
                          <p className="font-medium text-gray-500 uppercase">Fecha de Salida</p>
                          <p className="text-gray-900 font-semibold text-[#DC143C]">{new Date().toLocaleDateString('es-ES')}</p>
                        </div>
                        <div>
                          <p className="font-medium text-gray-500 uppercase">Hora de Salida</p>
                          <p className="text-gray-900 font-semibold text-[#DC143C]">{new Date().toLocaleTimeString('es-ES')}</p>
                        </div>
                      </div>
                      <p className="text-xs text-orange-600 italic mt-2">La hora de salida se guarda automáticamente al presionar el botón Terminar</p>
                    </div>
                    <div>
                      <Label htmlFor="guard_name_exit">Nombre del Vigilante</Label>
                      <Input
                        id="guard_name_exit"
                        data-testid="guard-name-exit-input"
                        value={guardName}
                        onChange={(e) => setGuardName(e.target.value)}
                        placeholder="Ingrese su nombre"
                        className="mt-1 h-11"
                      />
                    </div>
                    <div className="flex gap-2 justify-end pt-2">
                      <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
                      <Button
                        data-testid="confirm-exit-button"
                        onClick={handleApproval}
                        disabled={loading}
                        className="bg-green-600 hover:bg-green-700 text-white font-medium uppercase"
                      >
                        {loading ? 'Procesando...' : 'Terminar'}
                      </Button>
                    </div>
                  </div>
                )}

                {/* Entry Approval */}
                {actionType === 'entry' && (
                  <div className="space-y-4">
                    <div className="bg-gray-50 p-4 rounded-md space-y-2 text-sm">
                      <p><strong>Visitante:</strong> {selectedVisit.visitor_name}</p>
                      <p><strong>Empresa:</strong> {selectedVisit.visitor_company}</p>
                      <p><strong>Asunto:</strong> {selectedVisit.subject}</p>
                    </div>
                    <div>
                      <Label htmlFor="guard_name_entry">Nombre del Vigilante</Label>
                      <Input
                        id="guard_name_entry"
                        data-testid="guard-name-approval-input"
                        value={guardName}
                        onChange={(e) => setGuardName(e.target.value)}
                        placeholder="Ingrese su nombre"
                        className="mt-1 h-11"
                      />
                    </div>
                    <div className="flex gap-2 justify-end pt-2">
                      <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
                      <Button
                        data-testid="confirm-approval-button"
                        onClick={handleApproval}
                        disabled={loading}
                        className="bg-[#DC143C] hover:bg-[#B91C1C] text-white"
                      >
                        {loading ? 'Aprobando...' : 'Confirmar'}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
